import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import AuthImage from "../components/AuthImage";
import Lightbox from "../components/Lightbox";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import ScanTrigger from "../components/ScanTrigger";

export default function Stock() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [lots, setLots] = useState([]);
  const [q, setQ] = useState("");
  const [lightbox, setLightbox] = useState(null); // {photos, startIndex, title}

  const catName = params.get("cat") || "";
  const sizeId = params.get("size") || "";
  const colorId = params.get("color") || "";
  const selectedCat = useMemo(() => cats.find(c => c.name === catName), [cats, catName]);
  const catId = selectedCat?.id || "";

  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);

  useEffect(() => {
    if (!selectedCat) { setSizes([]); setColors([]); return; }
    api.get("/sizes", { params: { category_id: selectedCat.id } }).then(r => setSizes(r.data));
    api.get("/colors", { params: { category_id: selectedCat.id } }).then(r => setColors(r.data));
  }, [selectedCat]);

  useEffect(() => {
    // Wait until categories load if a category filter is requested
    if (catName && !catId) return;
    const p = {};
    if (catId) p.category_id = catId;
    if (sizeId) p.size_id = sizeId;
    if (colorId) p.color_id = colorId;
    if (q.trim()) p.search = q.trim();
    api.get("/lots", { params: p }).then(r => setLots(r.data));
  }, [catId, catName, sizeId, colorId, q]);

  const setFilter = (patch) => {
    const nx = { cat: catName, size: sizeId, color: colorId, ...patch };
    const clean = Object.fromEntries(Object.entries(nx).filter(([_, v]) => v));
    setParams(clean);
  };
  const clearAll = () => { setParams({}); setQ(""); };

  return (
    <div className="space-y-8" data-testid="stock-page">
      <header className="flex flex-wrap gap-3 items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">Inventory</div>
          <h1 className="font-display font-bold text-4xl tracking-tight text-[#2D2622]">Stock on the floor.</h1>
        </div>
      </header>

      {/* Search + Scan */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[280px] max-w-2xl">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B6F47]" />
          <input data-testid="stock-search" placeholder="Search colour, lot, rack, size or barcode…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full h-11 pl-10 pr-10 bg-white border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B6F47]"><X size={14}/></button>
          )}
        </div>
        <button data-testid="stock-scan-btn"
          className="h-11 px-4 border border-[#E5D8C4] bg-white text-xs uppercase tracking-[0.2em] hover:border-[#2D2622] inline-flex items-center gap-2 p-0"
          style={{padding: 0}}>
          <div className="w-full h-full"><ScanTrigger inline /></div>
        </button>
        {(catName || sizeId || colorId) && (
          <button onClick={clearAll} data-testid="stock-clear-btn"
            className="h-11 px-3 text-xs uppercase tracking-[0.2em] text-[#8B6F47] hover:text-[#2D2622]">
            Clear filters
          </button>
        )}
      </div>

      {/* Cascading filters */}
      <div className="space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-2">1 · Category</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setParams({})} data-testid="filter-all"
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${!catName?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
              All ({lots.length})
            </button>
            {cats.map(c => (
              <button key={c.id} onClick={() => setParams({ cat: c.name })}
                data-testid={`filter-${c.name.toLowerCase().replace(/\s+/g,'-')}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${catName===c.name?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {selectedCat && sizes.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-2">2 · Size (optional)</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilter({ size: "" })}
                className={`px-3 py-1.5 text-xs font-mono2 border ${!sizeId?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>All sizes</button>
              {sizes.map(s => (
                <button key={s.id} onClick={() => setFilter({ size: s.id })}
                  data-testid={`filter-size-${s.id}`}
                  className={`px-3 py-1.5 text-xs font-mono2 border ${sizeId===s.id?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedCat && colors.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-2">3 · Colour (optional)</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilter({ color: "" })}
                className={`px-3 py-1.5 text-xs border ${!colorId?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>All colours</button>
              {colors.map(c => (
                <button key={c.id} onClick={() => setFilter({ color: c.id })}
                  data-testid={`filter-color-${c.id}`}
                  className={`px-3 py-1.5 text-xs border flex items-center gap-2 ${colorId===c.id?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
                  {c.hex_code && <span className="w-3 h-3 border border-white/40" style={{background:c.hex_code}}/>}
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="text-[11px] text-[#8B6F47] font-mono2">
        {lots.length} lot{lots.length===1?"":"s"} shown
      </div>
      {lots.length === 0 ? (
        <div className="border border-dashed border-[#D4C5B0] p-12 text-center bg-white">
          <div className="text-[#8B6F47] text-sm">No lots match this filter.</div>
          <Link to="/add" className="inline-block mt-4 px-5 py-2.5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em]">+ Add first lot</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
          {lots.map(l => (
            <Link to={`/lot/${l.id}`} key={l.id} data-testid={`lot-card-${l.id}`}
              className="group bg-white border border-[#E5D8C4] hover:border-[#2D2622] transition-colors overflow-hidden">
              <div className="aspect-square w-full bg-[#F3ECDF] overflow-hidden relative tl-swap"
                onClick={(e) => {
                  if (!l.photos?.length) return;
                  e.preventDefault(); e.stopPropagation();
                  setLightbox({ photos: l.photos, startIndex: 0, title: `Lot ${l.lot_no}` });
                }}>
                {l.photos?.[0]?.storage_path ? (
                  <>
                    <AuthImage path={l.photos[0].storage_path} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {l.category_name === "Translucent" && l.photos?.[1]?.storage_path && (
                      <div className="tl-hover w-full h-full">
                        <AuthImage path={l.photos[1].storage_path} className="w-full h-full object-cover" />
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/60 text-white text-[9px] uppercase tracking-widest">Backlit</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#A8998A] text-xs">No photo</div>
                )}
                <div className="absolute top-2 left-2 px-2 py-1 bg-white/95 backdrop-blur text-[9px] uppercase tracking-[0.25em] font-semibold text-[#2D2622]">
                  {l.category_name}
                </div>
                <div className="absolute top-2 right-2 px-2 py-1 bg-[#2D2622] text-white text-[10px] font-mono2 font-bold">
                  {l.rack_no}
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-base text-[#2D2622] truncate">{l.color_name}</div>
                    <div className="text-xs text-[#8B6F47] font-mono2 mt-0.5 truncate">{l.size_label}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono2 text-xl font-bold text-[#2D2622] leading-none">{l.quantity}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B6F47] mt-1">pcs</div>
                  </div>
                </div>
                <div className="mt-3 flex justify-between items-center pt-3 border-t border-[#F3ECDF]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B6F47]">Lot</div>
                  <div className="font-mono2 text-xs font-semibold text-[#2D2622]">{l.lot_no}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {lightbox && (
        <Lightbox photos={lightbox.photos} startIndex={lightbox.startIndex}
          title={lightbox.title} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
